/**
 * Korvattu rxfire/database-kirjasto paikallisella toteutuksella,
 * koska rxfire ei toimi uusimman Angularin kanssa.
 *
 * Alkuperäinen lähde: https://github.com/FirebaseExtended/rxfire
 * Lisenssi: Apache-2.0
 */

import { onChildAdded, onChildChanged, onChildMoved, onChildRemoved, onValue, get as databaseGet, Query, DataSnapshot } from 'firebase/database'
import { Observable, of, merge, from } from 'rxjs'
import { delay, switchMap, scan, distinctUntilChanged, map } from 'rxjs/operators'

export enum ListenEvent {
  added = 'child_added',
  removed = 'child_removed',
  changed = 'child_changed',
  moved = 'child_moved',
  value = 'value',
}

interface QueryChange {
  snapshot: DataSnapshot
  prevKey: string | null | undefined
  event: ListenEvent
}

const ListenerMethods = Object.freeze({
  [ListenEvent.added]: onChildAdded,
  [ListenEvent.removed]: onChildRemoved,
  [ListenEvent.changed]: onChildChanged,
  [ListenEvent.moved]: onChildMoved,
  [ListenEvent.value]: onValue,
})

function fromRef(ref: Query, event: ListenEvent): Observable<QueryChange> {
  return new Observable<QueryChange>(subscriber => {
    const listenerFn = ListenerMethods[event] as any
    const unsubscribe = listenerFn(
      ref,
      (snapshot: DataSnapshot, prevKey?: string | null) => {
        subscriber.next({ snapshot, prevKey, event })
      },
      (error: Error) => subscriber.error(error),
    )
    return unsubscribe
  }).pipe(
    // Varmistetaan, että subscribe on asynkroninen. Firebase SDK:n on/once
    // callbackit voivat tulla synkronisesti.
    delay(0),
  )
}

function changeToData(change: QueryChange, options: { keyField?: string } = {}): unknown {
  const val = change.snapshot.val()
  if (!change.snapshot.exists()) {
    return val
  }
  if (typeof val !== 'object') {
    return val
  }
  return {
    ...val,
    ...(options.keyField ? { [options.keyField]: change.snapshot.key } : null),
  }
}

export function objectVal<T>(query: Query, options: { keyField?: string } = {}): Observable<T> {
  return fromRef(query, ListenEvent.value).pipe(map(change => changeToData(change, options) as T))
}

function validateEventsArray(events?: ListenEvent[]): ListenEvent[] {
  if (events == null || events.length === 0) {
    events = [ListenEvent.added, ListenEvent.removed, ListenEvent.changed, ListenEvent.moved]
  }
  return events
}

function get(query: Query): Observable<QueryChange> {
  return from(databaseGet(query)).pipe(
    map(snapshot => {
      return { snapshot, prevKey: null, event: ListenEvent.value }
    }),
  )
}

function positionFor(changes: QueryChange[], key: string | null): number {
  for (let i = 0; i < changes.length; i++) {
    if (changes[i].snapshot.key === key) {
      return i
    }
  }
  return -1
}

function positionAfter(changes: QueryChange[], prevKey?: string): number {
  if (prevKey == null) {
    return 0
  }
  const i = positionFor(changes, prevKey)
  if (i === -1) {
    return changes.length
  }
  return i + 1
}

function buildView(current: QueryChange[], change: QueryChange): QueryChange[] {
  const { snapshot, prevKey, event } = change
  const { key } = snapshot
  const currentKeyPosition = positionFor(current, key)
  const afterPreviousKeyPosition = positionAfter(current, prevKey || undefined)

  switch (event) {
    case ListenEvent.value:
      if (change.snapshot && change.snapshot.exists()) {
        let prevKey: string | null = null
        change.snapshot.forEach(snapshot => {
          const action: QueryChange = {
            snapshot,
            event: ListenEvent.value,
            prevKey,
          }
          prevKey = snapshot.key
          current = [...current, action]
          return false
        })
      }
      return current
    case ListenEvent.added:
      if (currentKeyPosition > -1) {
        const previous = current[currentKeyPosition - 1]
        if (((previous && previous.snapshot.key) || null) !== prevKey) {
          current = current.filter(x => x.snapshot.key !== snapshot.key)
          current.splice(afterPreviousKeyPosition, 0, change)
        }
      } else if (prevKey == null) {
        return [change, ...current]
      } else {
        current = current.slice()
        current.splice(afterPreviousKeyPosition, 0, change)
      }
      return current
    case ListenEvent.removed:
      return current.filter(x => x.snapshot.key !== snapshot.key)
    case ListenEvent.changed:
      return current.map(x => (x.snapshot.key === key ? change : x))
    case ListenEvent.moved:
      if (currentKeyPosition > -1) {
        const data = current.splice(currentKeyPosition, 1)[0]
        current = current.slice()
        current.splice(afterPreviousKeyPosition, 0, data)
        return current
      }
      return current
    default:
      return current
  }
}

export function list(query: Query, options: { events?: ListenEvent[] } = {}): Observable<QueryChange[]> {
  const events = validateEventsArray(options.events)
  return get(query).pipe(
    switchMap(change => {
      const childEvent$ = [of(change)]
      events.forEach(event => {
        childEvent$.push(fromRef(query, event))
      })
      return merge(...childEvent$).pipe(scan(buildView, []))
    }),
    distinctUntilChanged(),
  )
}
